import { useEffect, useState } from "react";
import { api, ApiError, isStepupRequired } from "@/api/client";
import type { LandingDetail, LandingSummary } from "@/lib/types";
import { useApp } from "@/App";

const NEW = -1;

type Form = {
  slug: string;
  name: string;
  prod_domain: string;
  test_domain: string;
  tags: string;
  build_mode: "static" | "build";
  build_command: string;
  output_dir: string;
};

const EMPTY: Form = {
  slug: "",
  name: "",
  prod_domain: "",
  test_domain: "",
  tags: "",
  build_mode: "static",
  build_command: "",
  output_dir: ".",
};

export function Settings() {
  const { toast } = useApp();
  const [list, setList] = useState<LandingSummary[]>([]);
  const [selected, setSelected] = useState<number>(NEW);
  const [form, setForm] = useState<Form>(EMPTY);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const reloadList = () => api.listLandings().then(setList);
  useEffect(() => {
    reloadList();
  }, []);

  const loadForm = (id: number) => {
    if (id === NEW) {
      setForm(EMPTY);
      return;
    }
    api.getLanding(id).then((d: LandingDetail) =>
      setForm({
        slug: d.slug,
        name: d.name,
        prod_domain: d.prod_domain,
        test_domain: d.test_domain,
        tags: d.tags.join(", "),
        build_mode: d.build_mode,
        build_command: d.build_command ?? "",
        output_dir: d.output_dir,
      }),
    );
  };

  useEffect(() => {
    loadForm(selected);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected]);

  const set = (k: keyof Form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    setErr(null);
    const tags = form.tags.split(",").map((t) => t.trim()).filter(Boolean);
    try {
      if (selected === NEW) {
        const created = await api.createLanding({
          slug: form.slug,
          name: form.name,
          prod_domain: form.prod_domain,
          test_domain: form.test_domain,
          tags,
          build_mode: form.build_mode,
          build_command: form.build_command || null,
          output_dir: form.output_dir || ".",
        });
        await reloadList();
        setSelected(created.id);
        toast("Сайт создан.");
      } else {
        await api.updateLanding(selected, {
          name: form.name,
          prod_domain: form.prod_domain,
          test_domain: form.test_domain,
          tags,
          build_mode: form.build_mode,
          build_command: form.build_command || null,
          output_dir: form.output_dir || ".",
        });
        await reloadList();
        toast("Сохранено.");
      }
    } catch (e) {
      setErr(e instanceof ApiError ? String(e.message) : "Не удалось сохранить.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (selected === NEW) return;
    if (!confirm(`Удалить сайт «${form.name}»? Действие нельзя отменить.`)) return;
    try {
      await api.deleteLanding(selected);
    } catch (e) {
      if (isStepupRequired(e)) {
        try {
          await api.stepup();
        } catch {
          window.location.href = "/api/auth/login?stepup=1";
          return;
        }
        await api.deleteLanding(selected).catch(() => undefined);
      } else {
        toast("Не удалось удалить.");
        return;
      }
    }
    await reloadList();
    setSelected(NEW);
    toast("Сайт удалён.");
  };

  return (
    <div className="wrap">
      <h1>Настройка сайтов</h1>
      <p className="lead">Названия, адреса и режим сборки. Создание новых сайтов настраивает IT.</p>

      <div className="card" style={{ padding: 24 }}>
        <div className="field" style={{ marginBottom: 18 }}>
          <label>Выберите сайт</label>
          <select value={selected} onChange={(e) => setSelected(Number(e.target.value))}>
            {list.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
            <option value={NEW}>+ Добавить новый</option>
          </select>
        </div>

        <div className="frm">
          {selected === NEW && (
            <div className="field">
              <label>Идентификатор (slug)</label>
              <input value={form.slug} onChange={(e) => set("slug", e.target.value)} placeholder="summer" />
              <div className="hint">Только латиница, цифры и дефис. Менять потом нельзя.</div>
            </div>
          )}
          <div className="field">
            <label>Название</label>
            <input value={form.name} onChange={(e) => set("name", e.target.value)} />
            <div className="hint">Видно только вам в этом списке.</div>
          </div>
          <div className="field">
            <label>Адрес сайта</label>
            <input value={form.prod_domain} onChange={(e) => set("prod_domain", e.target.value)} placeholder="summer.shop-pc.ru" />
            <div className="hint">По этому адресу сайт открывают клиенты. A-запись DNS заводит IT вручную.</div>
          </div>
          <div className="field">
            <label>Метки</label>
            <input value={form.tags} onChange={(e) => set("tags", e.target.value)} placeholder="акции, лето" />
            <div className="hint">Через запятую. Чтобы легче было искать в списке.</div>
          </div>

          <details className="adv">
            <summary>Технические настройки — для IT</summary>
            <div className="inner">
              <div className="field">
                <label>Адрес для предпросмотра</label>
                <input value={form.test_domain} onChange={(e) => set("test_domain", e.target.value)} placeholder="summer.test.shop-pc.ru" />
              </div>
              <div className="field">
                <label>Как готовить файл</label>
                <select value={form.build_mode} onChange={(e) => set("build_mode", e.target.value)}>
                  <option value="static">Файл уже готов (по умолчанию)</option>
                  <option value="build">Собирать на сервере</option>
                </select>
              </div>
              {form.build_mode === "build" && (
                <div className="field">
                  <label>Команда сборки</label>
                  <input value={form.build_command} onChange={(e) => set("build_command", e.target.value)} placeholder="npm run build" />
                </div>
              )}
              <div className="field">
                <label>Папка сайта внутри архива</label>
                <input value={form.output_dir} onChange={(e) => set("output_dir", e.target.value)} placeholder="dist" />
              </div>
              <div className="hint">
                📊 Сбор статистики подключается автоматически при публикации — отдельно
                ничего прописывать в сайте не нужно. Считается только трафик с боевого адреса,
                предпросмотр в метрики не попадает.
              </div>
            </div>
          </details>
        </div>

        {err && <div className="err" style={{ marginTop: 14 }}>{err}</div>}

        <div className="rowend" style={{ marginTop: 22 }}>
          <button className="btn" onClick={() => loadForm(selected)} disabled={busy}>Отменить</button>
          <button className="btn primary" onClick={save} disabled={busy}>
            {selected === NEW ? "Создать" : "Сохранить"}
          </button>
        </div>

        {selected !== NEW && (
          <div className="danger">
            <div className="small">
              <b style={{ color: "var(--danger)" }}>Удалить сайт.</b>{" "}
              <span className="muted">Сайт перестанет открываться по своему адресу. Действие нельзя отменить.</span>
            </div>
            <button className="btn ghostdanger sm" onClick={remove}>Удалить…</button>
          </div>
        )}
      </div>
    </div>
  );
}
